'use client';

import React, { useEffect, useRef, useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import TransitionLink, { usePageFadeIn } from '@/components/ui/TransitionLink';

function WeddingPageInner() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const searchParams = useSearchParams();

  const [videoEnded, setVideoEnded] = useState(false);
  const [showText, setShowText] = useState(false);
  const [menuPhase, setMenuPhase] = useState<'idle' | 'cinematic' | 'menu'>('idle');
  const [introPhase, setIntroPhase] = useState<'hold' | 'defocus' | 'refocus' | 'clear'>('hold');

  usePageFadeIn();

  function startIntroSequence() {
    setTimeout(() => {
      setIntroPhase('defocus');
      setTimeout(() => {
        setIntroPhase('refocus');
        setTimeout(() => {
          setIntroPhase('clear');
        }, 1200);
      }, 800);
    }, 2500);
  }

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Skip to menu directly when ?menu=1 param is present
    const isMenu = searchParams.get('menu') === '1';
    if (isMenu) {
      setVideoEnded(true);
      setShowText(false);
      setMenuPhase('menu');
      setIntroPhase('clear');
      video.pause();
      return;
    }

    // Reset all states when replay param is present
    const isReplay = searchParams.get('replay') === '1';
    if (isReplay) {
      setVideoEnded(false);
      setShowText(false);
      setMenuPhase('idle');
      setIntroPhase('hold');
      video.currentTime = 0;
    }

    let introStarted = false;
    const handlePlaying = () => {
      if (introStarted) return;
      introStarted = true;
      startIntroSequence();
    };

    const handleEnded = () => {
      setVideoEnded(true);
      setTimeout(() => setShowText(true), 300);
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('playing', handlePlaying);

    // Try autoplay; if blocked, set up a silent tap-to-play on the whole screen
    video.play().catch(() => {
      const handleTap = () => {
        if (video.paused) {
          video.play().catch(() => {});
        }
        document.removeEventListener('touchstart', handleTap);
        document.removeEventListener('click', handleTap);
      };
      document.addEventListener('touchstart', handleTap, { passive: true });
      document.addEventListener('click', handleTap);
    });

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [searchParams]);

  function handleButtonClick() {
    setMenuPhase('cinematic');
    setTimeout(() => {
      setMenuPhase('menu');
    }, 600);
  }

  function handleIntroReplay() {
    const video = videoRef.current;
    if (!video) return;
    setVideoEnded(false);
    setShowText(false);
    setMenuPhase('idle');
    setIntroPhase('hold');
    video.currentTime = 0;
    video.play().catch(() => {});
    startIntroSequence();
  }

  const menuItems = [
    {
      label: 'Присутствие',
      href: '/rsvp',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      ),
      hint: 'подтвердить участие',
      accent: 'rgba(212,171,92,0.18)',
      borderAccent: 'rgba(212,171,92,0.55)',
    },
    {
      label: 'Расписание',
      href: '/schedule',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 7 12 12 15.5 15.5"/>
        </svg>
      ),
      hint: 'программа дня',
      accent: 'rgba(212,171,92,0.18)',
      borderAccent: 'rgba(212,171,92,0.55)',
    },
    {
      label: 'Дресс-код',
      href: '/dresscode',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ),
      hint: 'стиль вечера',
      accent: 'rgba(212,171,92,0.18)',
      borderAccent: 'rgba(212,171,92,0.55)',
    },
    {
      label: 'Контакты',
      href: '/contacts',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.81a16 16 0 0 0 6 6l.86-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.5 16.92z"/>
        </svg>
      ),
      hint: 'связаться с нами',
      accent: 'rgba(212,171,92,0.18)',
      borderAccent: 'rgba(212,171,92,0.55)',
    },
  ];

  const videoBlurMap = {
    hold: '18px',
    defocus: '28px',
    refocus: '0px',
    clear: '0px',
  };
  const videoBlurTransitionMap = {
    hold: 'none',
    defocus: 'filter 0.8s cubic-bezier(0.4, 0, 0.6, 1)',
    refocus: 'filter 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    clear: 'none',
  };
  const videoBlur = videoBlurMap[introPhase];
  const videoBlurTransition = videoBlurTransitionMap[introPhase];

  const overlayBgMap = {
    hold: 'rgba(0,0,0,0.88)',
    defocus: 'rgba(0,0,0,0.92)',
    refocus: 'rgba(0,0,0,0.45)',
    clear: videoEnded
      ? 'linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.75))'
      : 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55))',
  };
  const overlayTransitionMap = {
    hold: 'none',
    defocus: 'background 0.8s ease',
    refocus: 'background 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    clear: 'background 0.8s ease',
  };
  const overlayBg = overlayBgMap[introPhase];
  const overlayTransition = overlayTransitionMap[introPhase];

  const logoTop = introPhase === 'hold' || introPhase === 'defocus' ? '50%' : '26px';
  const logoTransform = introPhase === 'hold' || introPhase === 'defocus' ? 'translate(-50%, -50%)' : 'translateX(-50%)';
  const logoOpacity = introPhase === 'defocus' ? 0 : 1;
  const logoTransitionMap = {
    hold: 'none',
    defocus: 'opacity 0.7s cubic-bezier(0.4, 0, 0.6, 1)',
    refocus: 'opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s',
    clear: 'none',
  };
  const logoTransition = logoTransitionMap[introPhase];

  return (
    <div style={{
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      minHeight: '100vh',
      WebkitFontSmoothing: 'antialiased',
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* ===== VIDEO BACKGROUND ===== */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
      } as React.CSSProperties}>
        <video
          ref={videoRef}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          autoPlay
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            imageRendering: 'auto',
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            transform: 'translate3d(0, 0, 0)',
            filter: `blur(${videoBlur})`,
            transition: videoBlurTransition,
            willChange: 'filter, transform',
            WebkitTransform: 'translate3d(0, 0, 0)',
            isolation: 'isolate',
            contain: 'strict',
          } as React.CSSProperties}
        >
          <source
            src="https://static.tildacdn.ink/vide6337-3138-4537-b036-623932346531/Gen-4_5_3823157658ge.mp4"
            type="video/mp4"
          />
        </video>

        {/* Gradient overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: overlayBg,
          transition: overlayTransition,
          zIndex: 1
        }} />
      </div>

      {/* ===== LOGO ===== */}
      <div style={{
        position: 'fixed',
        left: '50%',
        zIndex: 10,
        opacity: logoOpacity,
        transition: logoTransition,
        width: '119px',
        height: '79px',
        top: logoTop,
        transform: logoTransform,
        willChange: 'opacity',
      } as React.CSSProperties}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="https://static.tildacdn.ink/tild3532-3436-4962-b337-653236343832/noroot.png"
          alt="Ashot Goar"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </div>

      {/* ===== INTRO LINK UNDER LOGO (menu phase) ===== */}
      {menuPhase === 'menu' && (
        <div style={{
          position: 'fixed',
          left: '50%',
          top: '115px',
          transform: 'translateX(-50%)',
          zIndex: 35,
          display: 'flex',
          justifyContent: 'center',
        }}>
          <button
            onClick={handleIntroReplay}
            style={{
              fontFamily: "'Melodrama', Arial, sans-serif",
              fontSize: '11px',
              color: 'rgba(212,171,92,0.6)',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'color 0.3s ease',
              whiteSpace: 'nowrap',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <span style={{ fontSize: '14px' }}>↺</span>
            <span>Интро</span>
          </button>
        </div>
      )}

      {/* ===== POST-VIDEO CONTENT ===== */}
      {videoEnded && menuPhase === 'idle' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 15,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '0 20px'
        }}>
          {/* Text — line by line cinematic reveal */}
          <div
            className="wedding-title"
            style={{ opacity: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15em' }}
          >
            {['Москва.', '17 апреля.', 'С любовью.'].map((line, i) => (
              <p
                key={line}
                style={{
                  margin: 0,
                  textAlign: 'center',
                  opacity: showText ? 1 : 0,
                  transform: showText ? 'translateY(0)' : 'translateY(18px)',
                  transition: `opacity 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${i * 0.55}s, transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${i * 0.55}s`,
                }}
              >
                {line}
              </p>
            ))}
          </div>

          {/* Button */}
          <div style={{
            marginTop: '36px',
            opacity: showText ? 1 : 0,
            transform: showText ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 1.0s ease 1.9s, transform 1.0s ease 1.9s'
          }}>
            <button
              className="wedding-btn"
              type="button"
              onClick={handleButtonClick}
            >
              <span className="wedding-btn-glow" />
              <span className="wedding-btn-inner">
                <span className="btn-text">Стать частью торжества</span>
                <span className="wedding-btn-arrow">→</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* ===== CINEMATIC FADE TO BLACK ===== */}
      {(menuPhase === 'cinematic' || menuPhase === 'menu') && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 28,
          background: '#000000',
          opacity: menuPhase === 'cinematic' ? 1 : 0,
          transition: menuPhase === 'cinematic' ? 'opacity 0.55s cubic-bezier(0.4, 0, 0.2, 1)' : 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: 'none'
        }} />
      )}

      {/* ===== MENU BUTTONS ===== */}
      {menuPhase === 'menu' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '14px',
          padding: '20px'
        }}>
          {menuItems.map((item, i) => (
            <TransitionLink
              key={item.label}
              href={item.href}
              className="menu-card-btn"
              style={{
                animationDelay: `${0.1 + i * 0.07}s`,
                '--btn-accent': item.accent,
                '--btn-border': item.borderAccent,
              } as React.CSSProperties}
            >
              <span className="menu-card-icon">{item.icon}</span>
              <span className="menu-card-body">
                <span className="menu-card-label">{item.label}</span>
                <span className="menu-card-hint">{item.hint}</span>
              </span>
              <span className="menu-card-arrow">→</span>
            </TransitionLink>
          ))}
        </div>
      )}

      <style>{`
        @keyframes menuCardIn {
          0%   { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tapPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.96); }
        }
      `}</style>
    </div>
  );
}

export default function WeddingPage() {
  return (
    <Suspense fallback={null}>
      <WeddingPageInner />
    </Suspense>
  );
}
